import { Injectable } from '@angular/core';

import { membersCreate, membersDeactivate, membersList, membersResendInvite } from '@api/sdk.gen';
import type { CreateMemberRequest, ListMembersResponse, MemberItem } from '@api';

import type { ManagedTeacherUser } from '../../models/managed-teacher-user';
import { memberItemsToManagedTeacherUsers } from './members.api.mapper';

export class MembersApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly kind: 'http' | 'empty-body' | 'transport',
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'MembersApiError';
  }
}

interface FieldsResult<TData> {
  data?: TData;
  error?: unknown;
  response?: { readonly status: number; readonly ok: boolean };
}

@Injectable({ providedIn: 'root' })
export class MembersApiService {
  list(): Promise<ListMembersResponse> {
    return membersList({
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => MembersApiService.unwrap(result, 'Failed to load members.'));
  }

  listManagedTeachers(): Promise<ManagedTeacherUser[]> {
    return this.list().then((response) => memberItemsToManagedTeacherUsers(response.items ?? []));
  }

  create(body: CreateMemberRequest): Promise<MemberItem> {
    return membersCreate({
      body,
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => MembersApiService.unwrap(result, 'Failed to invite member.'));
  }

  resendInvite(userId: string): Promise<void> {
    return membersResendInvite({
      path: { userId },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => {
      MembersApiService.assertOk(result, 'Failed to resend invite.');
    });
  }

  deactivate(userId: string): Promise<void> {
    return membersDeactivate({
      path: { userId },
      throwOnError: false,
      responseStyle: 'fields' as const,
    }).then((result) => {
      MembersApiService.assertOk(result, 'Failed to deactivate member.');
    });
  }

  private static assertOk(result: FieldsResult<unknown>, message: string): number {
    const { response } = result;

    if (response === undefined || response.status === 0) {
      throw new MembersApiError(message, 0, 'transport', result.error);
    }

    if (response.ok !== true) {
      throw new MembersApiError(message, response.status, 'http', result.error);
    }

    return response.status;
  }

  private static unwrap<TData>(result: FieldsResult<TData>, message: string): TData {
    const status = MembersApiService.assertOk(result, message);

    if (result.data == null) {
      throw new MembersApiError(message, status, 'empty-body', result.error);
    }

    return result.data;
  }
}
